import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { VersionChecker } from "@/components/VersionChecker";
import { ManualRefreshTrigger } from "@/components/ManualRefreshTrigger";
import { RealtimeProvider } from "@/components/RealtimeProvider";
import { AuthProvider } from "@/hooks/useAuth";
import { AdminAuthProvider } from "@/hooks/useAdminAuth";
import { CoachAuthProvider } from "@/hooks/useCoachAuth";
import { ParentAuthProvider } from "@/hooks/useParentAuth";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Player components
const PlayerLayout = React.lazy(() => import("./components/PlayerLayout"));
const PlayerDashboard = React.lazy(() => import("./pages/PlayerDashboard"));
const PlayerSession = React.lazy(() => import("./pages/PlayerSession"));
const PlayerProfile = React.lazy(() => import("./pages/PlayerProfile"));
const PlayerSkillStrategy = React.lazy(() => import("./pages/PlayerSkillStrategy"));
const PlayerCustomStrategy = React.lazy(() => import("./pages/PlayerCustomStrategy"));
const PlayerSkillStrategyActive = React.lazy(() => import("./pages/PlayerSkillStrategyActive"));
const PlayerCampLeaderboard = React.lazy(() => import("./pages/PlayerCampLeaderboard"));

// Lazy load pages to avoid potential circular dependencies
const CoachAdmin = React.lazy(() => import("./pages/CoachAdmin"));
const CoachAuth = React.lazy(() => import("./pages/CoachAuth"));
const CoachTestPage = React.lazy(() => import("./pages/CoachTestPage"));

// Coach components  
const CoachLayout = React.lazy(() => import("./components/CoachLayout"));
const CoachDashboard = React.lazy(() => import("./pages/CoachDashboard"));
const CoachPlayerManagement = React.lazy(() => import("./pages/CoachPlayerManagement"));
const CoachTeamManagement = React.lazy(() => import("./pages/CoachTeamManagement"));
const CoachSkillsStrategy = React.lazy(() => import("./pages/CoachSkillsStrategy"));
const CoachSettings = React.lazy(() => import("./pages/CoachSettings"));
const Galleries = React.lazy(() => import("./pages/Galleries"));
const S3FolderDetail = React.lazy(() => import("./pages/S3FolderDetail"));
const GalleryDetail = React.lazy(() => import("./pages/GalleryDetail"));

// Admin components
const AdminLayout = React.lazy(() => import("./components/AdminLayout"));
import AdminAuth from "./pages/AdminAuth";
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const AdminClubs = React.lazy(() => import("./pages/AdminClubs"));
const AdminUsers = React.lazy(() => import("./pages/AdminUsers"));
const AdminApprovals = React.lazy(() => import("./pages/AdminApprovals"));
const AdminClubManagement = React.lazy(() => import("./pages/AdminClubManagement"));
const AdminTeamManagement = React.lazy(() => import("./pages/AdminTeamManagement"));
const AdminCoachManagement = React.lazy(() => import("./pages/AdminCoachManagement"));
const AdminPlayerManagement = React.lazy(() => import("./pages/AdminPlayerManagement"));
const AdminParentManagement = React.lazy(() => import("./pages/AdminParentManagement"));
const AdminSkillsManagement = React.lazy(() => import("./pages/AdminSkillsManagement"));
const AdminSkillsStrategy = React.lazy(() => import("./pages/AdminSkillsStrategy"));
const AdminMediaManagement = React.lazy(() => import("./pages/AdminMediaManagement"));
const AdminAudioManagement = React.lazy(() => import("./pages/AdminAudioManagement"));
const AdminTournaments = React.lazy(() => import("./pages/AdminTournaments"));
const AdminTournamentForm = React.lazy(() => import("./pages/AdminTournamentForm"));
const AdminTournamentDetail = React.lazy(() => import("./pages/AdminTournamentDetail"));
const AdminEvents = React.lazy(() => import("./pages/AdminEvents"));
const AdminEventForm = React.lazy(() => import("./pages/AdminEventForm"));
const AdminEventDetail = React.lazy(() => import("./pages/AdminEventDetail"));
const AdminAwardBadges = React.lazy(() => import("./pages/AdminAwardBadges"));
const AdminBadgeManagement = React.lazy(() => import("./pages/AdminBadgeManagement"));
const AdminBackfillBadges = React.lazy(() => import("./pages/AdminBackfillBadges"));
const AdminSkillBackfill = React.lazy(() => import("./pages/AdminSkillBackfill"));
const AdminThumbnailMigration = React.lazy(() => import("./pages/AdminThumbnailMigration"));
const AdminCamps = React.lazy(() => import("./pages/AdminCamps"));
const AdminCampForm = React.lazy(() => import("./pages/AdminCampForm"));
const AdminCampDetail = React.lazy(() => import("./pages/AdminCampDetail"));

// Registration pages
const CoachRegister = React.lazy(() => import("./pages/CoachRegister"));
const PlayerRegister = React.lazy(() => import("./pages/PlayerRegister"));
const FindYourClub = React.lazy(() => import("./pages/FindYourClub"));
const Events = React.lazy(() => import("./pages/Events"));
const PublicEventDetail = React.lazy(() => import("./pages/PublicEventDetail"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const ContactUs = React.lazy(() => import("./pages/ContactUs"));

// Parent components
const ParentLayout = React.lazy(() => import("./components/ParentLayout"));
const ParentPlayerLayout = React.lazy(() => import("./components/parent/ParentPlayerLayout"));
const ParentAuth = React.lazy(() => import("./pages/ParentAuth"));
const ParentDashboard = React.lazy(() => import("./pages/ParentDashboard"));
const ParentPlayerDetail = React.lazy(() => import("./pages/ParentPlayerDetail"));
const ParentPasswordReset = React.lazy(() => import("./pages/ParentPasswordReset"));
const ParentEvents = React.lazy(() => import("./pages/ParentEvents"));
const ParentEventDetail = React.lazy(() => import("./pages/ParentEventDetail"));
const ParentEventsCalendar = React.lazy(() => import("./pages/ParentEventsCalendar"));
const ParentIDP = React.lazy(() => import("./pages/ParentIDP"));
const ParentSettings = React.lazy(() => import("./pages/ParentSettings"));
const ParentActivity = React.lazy(() => import("./pages/ParentActivity"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RealtimeProvider>
        <AuthProvider>
          <CoachAuthProvider>
            <AdminAuthProvider>
              <ParentAuthProvider>
                <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              
              {/* Find Your Club */}
              <Route 
                path="/find-club" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <FindYourClub />
                  </React.Suspense>
                } 
              />
              
              {/* Events Page */}
              <Route 
                path="/events" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <Events />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/events/:eventId" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PublicEventDetail />
                  </React.Suspense>
                } 
              />
              
              {/* Privacy Policy */}
              <Route 
                path="/privacy-policy" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PrivacyPolicy />
                  </React.Suspense>
                } 
              />
              
              {/* Terms and Conditions */}
              <Route 
                path="/terms-conditions" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <TermsOfService />
                  </React.Suspense>
                } 
              />
              
              {/* Contact Us */}
              <Route 
                path="/contact-us" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ContactUs />
                  </React.Suspense>
                } 
              />
              
              {/* Registration Routes */}
              <Route 
                path="/register/coach/:clubSlug" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CoachRegister />
                  </React.Suspense>
                }
              />
              <Route 
                path="/register/player/:clubSlug" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PlayerRegister />
                  </React.Suspense>
                }
              />
              
              {/* Player Routes */}
              <Route 
                path="/player" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PlayerLayout />
                  </React.Suspense>
                }
              >
                <Route 
                  index 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerDashboard />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="session" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerSession />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="skill-strategy" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerSkillStrategy />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="custom-strategy" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerCustomStrategy />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="profile" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerProfile />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camp/:campId" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <PlayerCampLeaderboard />
                    </React.Suspense>
                  } 
                />
              </Route>
              
              {/* Player Skill Strategy Active Session Route */}
              <Route 
                path="/players/skill-strategy/active" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PlayerSkillStrategyActive />
                  </React.Suspense>
                } 
              />
              
              <Route 
                path="/coach-auth"
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CoachAuth />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/coach-test" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CoachTestPage />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/coach" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <CoachLayout />
                  </React.Suspense>
                }
              >
                <Route 
                  index 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachDashboard />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="players" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachPlayerManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="teams" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachTeamManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="skills-strategy" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachSkillsStrategy />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="settings" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <CoachSettings />
                    </React.Suspense>
                  } 
                />
              </Route>
              {/* Parent Routes */}
              <Route 
                path="/parent/auth" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ParentAuth />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/parent/reset-password" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ParentPasswordReset />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/parent" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <ParentLayout />
                  </React.Suspense>
                }
              >
                <Route 
                  index 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <ParentDashboard />
                    </React.Suspense>
                  } 
                />
                {/* Per-player routes with tabs */}
                <Route 
                  path="player/:playerId" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <ParentPlayerLayout />
                    </React.Suspense>
                  }
                >
                  <Route index element={<React.Suspense fallback={<div>Loading...</div>}><ParentPlayerDetail /></React.Suspense>} />
                  <Route 
                    path="dashboard" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentPlayerDetail />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="activity" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentActivity />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="events" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentEvents />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="events/:eventId" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentEventDetail />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="calendar"
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentEventsCalendar />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="idp"
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentIDP />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="settings" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <ParentSettings />
                      </React.Suspense>
                    } 
                  />
                </Route>
              </Route>

              <Route 
                path="/galleries" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <Galleries />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/s3-folder/*" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <S3FolderDetail />
                  </React.Suspense>
                } 
              />
              <Route 
                path="/gallery/:galleryId" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <GalleryDetail />
                  </React.Suspense>
                } 
              />
              {/* Admin Routes */}
              <Route 
                path="/admin/auth" 
                element={<AdminAuth />} 
              />
              <Route 
                path="/admin" 
                element={
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <AdminLayout />
                  </React.Suspense>
                }
              >
                <Route 
                  index 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminDashboard />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="clubs" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminClubManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="teams" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminTeamManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="coaches" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCoachManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="players" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminPlayerManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="parents" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminParentManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="skills" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSkillsManagement />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="skills-strategy" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSkillsStrategy />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="approvals" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminApprovals />
                    </React.Suspense>
                  } 
                />
                <Route
                  path="media"
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminMediaManagement />
                    </React.Suspense>
                  } 
                 />
                 <Route
                   path="audio"
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminAudioManagement />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="users" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminUsers />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="tournaments" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminTournaments />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="tournaments/new" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminTournamentForm />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="tournaments/:id/edit" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminTournamentForm />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="tournaments/:id" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminTournamentDetail />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="events" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminEvents />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="events/new" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminEventForm />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="events/:id" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminEventDetail />
                     </React.Suspense>
                   } 
                 />
                 <Route 
                   path="events/:id/edit" 
                   element={
                     <React.Suspense fallback={<div>Loading...</div>}>
                       <AdminEventForm />
                     </React.Suspense>
                   } 
                 />
                  <Route 
                    path="award-badges" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <AdminAwardBadges />
                      </React.Suspense>
                    } 
                  />
                  <Route 
                    path="badges" 
                    element={
                      <React.Suspense fallback={<div>Loading...</div>}>
                        <AdminBadgeManagement />
                      </React.Suspense>
                    } 
                  />
                <Route 
                  path="backfill-badges" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminBackfillBadges />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="backfill-skills" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminSkillBackfill />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="thumbnail-migration" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminThumbnailMigration />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camps" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCamps />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camps/new" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCampForm />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camps/:id" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCampDetail />
                    </React.Suspense>
                  } 
                />
                <Route 
                  path="camps/:id/edit" 
                  element={
                    <React.Suspense fallback={<div>Loading...</div>}>
                      <AdminCampForm />
                    </React.Suspense>
                  } 
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
                </BrowserRouter>
                <VersionChecker />
                <ManualRefreshTrigger />
                <Toaster />
                <Sonner />
              </ParentAuthProvider>
            </AdminAuthProvider>
          </CoachAuthProvider>
        </AuthProvider>
      </RealtimeProvider>
    </QueryClientProvider>
  );
}

export default App;